
import { useState, useEffect } from 'react';
import { navigationItems } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { 
  LayoutGrid, 
  Calendar, 
  Brain, 
  Settings, 
  CreditCard, 
  HelpCircle,
  ChevronDown,
  ChevronUp,
  X,
  Menu,
  Ticket,
  Trophy
} from 'lucide-react';
import AnimatedLogo from './AnimatedLogo';
import { useLanguage } from '@/contexts/LanguageContext';

interface SidebarProps {
  isMobileMenuOpen: boolean;
  onCloseMobileMenu: () => void;
}

export const Sidebar = ({ isMobileMenuOpen, onCloseMobileMenu }: SidebarProps) => {
  const [activeItem, setActiveItem] = useState('tickets');
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [selectedMatchesCount, setSelectedMatchesCount] = useState(0);
  const { t } = useLanguage();
  
  useEffect(() => {
    const path = window.location.pathname;
    
    const pathToItemMap: Record<string, string> = {
      '/': 'dashboard',
      '/matches/daily-predictions': 'predictions',
      '/titan-engine': 'engine',
      '/tickets': 'tickets'
    };
    
    if (pathToItemMap[path]) {
      setActiveItem(pathToItemMap[path]);
    } else {
      const currentItem = navigationItems.find(item => 
        path.startsWith(item.href) && item.href !== '/'
      );
      
      if (currentItem) {
        setActiveItem(currentItem.id);
      }
    }

    const loadSelectedMatchesCount = () => {
      try {
        const storedMatches = localStorage.getItem('selectedMatches');
        if (storedMatches) {
          const parsedMatches = JSON.parse(storedMatches);
          setSelectedMatchesCount(parsedMatches.length);
        }
      } catch (error) {
        console.error("Error loading selected matches count:", error);
      }
    };
    
    loadSelectedMatchesCount();
    
    const handleMatchesUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const updatedMatches = customEvent.detail;
      setSelectedMatchesCount(updatedMatches.length);
    };
    
    window.addEventListener('selectedMatchesUpdated', handleMatchesUpdated);
    
    return () => {
      window.removeEventListener('selectedMatchesUpdated', handleMatchesUpdated);
    };
  }, []);

  const toggleSubmenu = () => {
    setSubmenuOpen(!submenuOpen);
  };

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'grid_view':
        return <LayoutGrid size={20} />;
      case 'today':
        return <Calendar size={20} />;
      case 'psychology':
        return <Brain size={20} />;
      case 'precision_manufacturing':
        return <Settings size={20} />;
      case 'trophy':
        return <Trophy size={20} />;
      case 'credit_card':
        return <CreditCard size={20} />;
      case 'bubble_chart':
        return <HelpCircle size={20} />;
      default:
        return <LayoutGrid size={20} />;
    }
  };

  const getNavigationLabel = (item: { id: string, label: string }) => {
    const labelMap: Record<string, string> = {
      'dashboard': t('nav.dashboard'),
      'predictions': t('nav.predictions'),
      'engine': t('nav.engine'),
      'tickets': t('nav.tickets'),
      'leaderboard': t('nav.leaderboard'),
      'monitoring': t('nav.monitoring'),
      'profile': t('nav.profile'),
      'settings': t('nav.settings'),
      'help': t('nav.help')
    };
    
    return labelMap[item.id] || item.label;
  };

  return (
    <aside 
      className={cn(
        "fixed top-0 left-0 z-40 h-full w-64 bg-card border-r border-border transition-transform duration-300 ease-in-out md:translate-x-0",
        isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      )}
    >
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <AnimatedLogo />
          <h2 className="text-lg font-semibold tracking-tight">Titan Prediction</h2>
        </div>
        <button
          onClick={onCloseMobileMenu}
          className="p-1 rounded-full hover:bg-muted md:hidden"
        >
          <X size={20} />
        </button>
      </div>
      
      <div className="sidebar flex flex-col h-[86vh] relative top-3 w-full">
        <nav className="space-y-1 px-3">
          {navigationItems.map((item) => {
            if (item.id === 'monitoring') {
              return (
                <div key={item.id} className="py-2">
                  <div 
                    className={cn(
                      "flex items-center gap-4 rounded-md px-3 py-2 cursor-pointer",
                      activeItem === item.id 
                        ? "bg-primary/10 text-primary" 
                        : "hover:bg-muted hover:text-foreground"
                    )}
                    onClick={toggleSubmenu}
                  >
                    <span className={cn(
                      "transition-all",
                      activeItem === item.id ? "text-primary" : "text-muted-foreground"
                    )}>
                      {getIconComponent(item.icon)}
                    </span>
                    <h3 className="font-medium whitespace-nowrap">{getNavigationLabel(item)}</h3>
                    <span className="ml-auto">
                      {submenuOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </span>
                  </div>
                  <div className={cn(
                    "pl-9 mt-1 space-y-1 overflow-hidden transition-all",
                    submenuOpen ? "max-h-40" : "max-h-0"
                  )}>
                    <a href="#" className={cn(
                      "block rounded-md px-3 py-2",
                      activeItem === 'stats' 
                        ? "bg-primary/10 text-primary" 
                        : "hover:bg-muted hover:text-foreground"
                    )}>Statistics</a>
                    <a href="#" className={cn(
                      "block rounded-md px-3 py-2",
                      activeItem === 'reports' 
                        ? "bg-primary/10 text-primary" 
                        : "hover:bg-muted hover:text-foreground"
                    )}>Reports</a>
                    <a href="#" className={cn(
                      "block rounded-md px-3 py-2",
                      activeItem === 'analysis' 
                        ? "bg-primary/10 text-primary" 
                        : "hover:bg-muted hover:text-foreground"
                    )}>Analysis</a>
                  </div>
                </div>
              );
            }
            
            return (
              <a
                key={item.id}
                href={item.href}
                className={cn(
                  "flex items-center gap-4 rounded-md px-3 py-2",
                  activeItem === item.id 
                    ? "bg-primary/10 text-primary" 
                    : "hover:bg-muted hover:text-foreground"
                )}
              >
                <span className={cn(
                  "transition-all",
                  activeItem === item.id ? "text-primary" : "text-muted-foreground"
                )}>
                  {getIconComponent(item.icon)}
                </span>
                <span className="text-base whitespace-nowrap">
                  {getNavigationLabel(item)}
                  {item.id === 'tickets' && selectedMatchesCount > 0 && (
                    <span className="ml-2 text-sm text-muted-foreground">
                      ({selectedMatchesCount})
                    </span>
                  )}
                </span>
              </a>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
